import { useState, useEffect, useRef, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, MagnifyingGlass, Trash, PencilSimple, Image as ImageIcon, X, 
  Buildings, PaintBrush, HardHat, Funnel
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import OptimizedImage from "@/components/OptimizedImage";
import { useSettings } from "@/contexts/SettingsContext";
import { PageHeader } from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GABINETE_TYPES = [
  { value: "ARQUITETOS", label: "Arquitetos", icon: Buildings, color: "bg-blue-500" },
  { value: "CONSTRUTORES", label: "Construtores", icon: HardHat, color: "bg-amber-500" },
  { value: "DESIGNERS", label: "Designers", icon: PaintBrush, color: "bg-purple-500" }
];

const emptyGabinete = {
  name: "",
  address: "",
  postal_code: "",
  gps_coordinates: "",
  city: "",
  country: "",
  contacts: [{ name: "", cargo: "", phone: "" }],
  email: "",
  website: "",
  social_media: "",
  perfil_interno: "",
  gabinete_type: "ARQUITETOS",
  logo_url: ""
};

export default function Promocao() {
  const { isReadOnly } = useSettings();
  const [gabinetes, setGabinetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingGabinete, setEditingGabinete] = useState(null);
  const [formData, setFormData] = useState(emptyGabinete);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const logoInputRef = useRef(null);

  const fetchGabinetes = useCallback(async () => {
    try {
      const params = filterType !== "all" ? { gabinete_type: filterType } : {};
      const response = await axios.get(`${API}/gabinetes`, { params });
      setGabinetes(response.data);
    } catch (error) {
      toast.error("Erro ao carregar gabinetes");
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchGabinetes();
  }, [fetchGabinetes]);

  const filteredGabinetes = gabinetes.filter(g => 
    g.name.toLowerCase().includes(search.toLowerCase()) ||
    (g.contact_person && g.contact_person.toLowerCase().includes(search.toLowerCase())) ||
    (g.contacts || []).some(c => c.name && c.name.toLowerCase().includes(search.toLowerCase()))
  );

  const openCreateDialog = () => {
    setEditingGabinete(null);
    setFormData(emptyGabinete);
    setLogoPreview(null);
    setPendingLogoFile(null);
    setShowDialog(true);
  };

  const openEditDialog = (gabinete) => {
    setEditingGabinete(gabinete);
    setFormData({
      name: gabinete.name || "",
      address: gabinete.address || "",
      postal_code: gabinete.postal_code || "",
      gps_coordinates: gabinete.gps_coordinates || "",
      city: gabinete.city || "",
      country: gabinete.country || "",
      contacts: (gabinete.contacts && gabinete.contacts.length) ? gabinete.contacts : [{ name: "", cargo: "", phone: "" }],
      email: gabinete.email || "",
      website: gabinete.website || "",
      social_media: gabinete.social_media || "",
      perfil_interno: gabinete.perfil_interno || "",
      gabinete_type: gabinete.gabinete_type || "ARQUITETOS",
      logo_url: gabinete.logo_url || ""
    });
    // Resolve logo URL for display
    const logoUrl = gabinete.logo_url || null;
    if (logoUrl && logoUrl.startsWith('/api/')) {
      setLogoPreview(`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`);
    } else {
      setLogoPreview(logoUrl);
    }
    setPendingLogoFile(null);
    setShowDialog(true);
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      if (editingGabinete) {
        // Existing gabinete: upload directly to GridFS
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        const response = await axios.post(
          `${API}/gabinetes/${editingGabinete.id}/logo`,
          uploadFormData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        const logoUrl = response.data.logo_url;
        setFormData(prev => ({ ...prev, logo_url: logoUrl }));
        setLogoPreview(`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`);
      } else {
        // New gabinete: store file for later upload after creation
        setPendingLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
      }
      toast.success("Logo carregado");
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Erro ao carregar logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: "" }));
    setLogoPreview(null);
    setPendingLogoFile(null);
  };

  const updateContact = (idx, field, value) => {
    setFormData(prev => {
      const contacts = [...prev.contacts];
      contacts[idx] = { ...contacts[idx], [field]: value };
      return { ...prev, contacts };
    });
  };

  const addContact = () => {
    setFormData(prev => ({ ...prev, contacts: [...prev.contacts, { name: "", cargo: "", phone: "" }] }));
  };

  const removeContact = (idx) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("O nome é obrigatório");
      return;
    }
    if (!formData.gabinete_type) {
      toast.error("Selecione o tipo de gabinete");
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData, contacts: formData.contacts.filter(c => c.name && c.name.trim()) };
      if (editingGabinete) {
        await axios.put(`${API}/gabinetes/${editingGabinete.id}`, payload);
        toast.success("Gabinete atualizado");
      } else {
        // Create gabinete first
        const response = await axios.post(`${API}/gabinetes`, payload);
        const newGabineteId = response.data.id;
        
        // Upload logo if pending
        if (pendingLogoFile && newGabineteId) {
          const logoFormData = new FormData();
          logoFormData.append('file', pendingLogoFile);
          await axios.post(
            `${API}/gabinetes/${newGabineteId}/logo`,
            logoFormData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        }
        
        toast.success("Gabinete criado");
      }
      setShowDialog(false);
      setPendingLogoFile(null);
      fetchGabinetes();
    } catch (error) {
      toast.error("Erro ao guardar gabinete");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/gabinetes/${deleteId}`);
      toast.success("Gabinete eliminado");
      setDeleteId(null);
      fetchGabinetes();
    } catch (error) {
      toast.error("Erro ao eliminar gabinete");
    }
  };

  const getTypeConfig = (type) => {
    return GABINETE_TYPES.find(t => t.value === type) || GABINETE_TYPES[0];
  };

  // Count by type
  const countByType = GABINETE_TYPES.map(type => ({
    ...type,
    count: gabinetes.filter(g => g.gabinete_type === type.value).length
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="promocao-page">
      {/* Header */}
      <PageHeader
        icon={Buildings}
        title="Promoção"
        subtitle="Gabinetes de Arquitetura, Construção e Design"
        actions={!isReadOnly && (
          <Button
            className="btn-primary"
            onClick={openCreateDialog}
            data-testid="btn-create-gabinete"
          >
            <Plus size={18} />
            Novo Gabinete
          </Button>
        )}
      />

      {/* Type Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {countByType.map((type) => {
          const IconComponent = type.icon;
          return (
            <button
              key={type.value}
              onClick={() => setFilterType(filterType === type.value ? "all" : type.value)}
              className={`gresart-card p-4 text-left transition-all ${
                filterType === type.value ? "ring-2 ring-[#009392]" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${type.color}`}>
                  <IconComponent size={20} className="text-white" weight="bold" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{type.count}</p>
                  <p className="text-sm text-stone-500">{type.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="search-input-container flex-1 max-w-md">
          <MagnifyingGlass size={18} />
          <Input
            placeholder="Pesquisar por nome, contacto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="gresart-input pl-10"
            data-testid="search-gabinetes"
          />
        </div>
        {filterType !== "all" && (
          <Button variant="outline" onClick={() => setFilterType("all")} className="gap-2">
            <X size={16} />
            Limpar filtro
          </Button>
        )}
      </div>

      {/* Gabinetes Grid */}
      {filteredGabinetes.length === 0 ? (
        <div className="gresart-card">
          <div className="empty-state">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
              <Buildings size={32} className="text-stone-400" />
            </div>
            <h3 className="text-lg font-medium text-stone-900 mb-2">
              {search || filterType !== "all" ? "Nenhum gabinete encontrado" : "Ainda não há gabinetes"}
            </h3>
            <p className="text-stone-500 mb-4">
              {search || filterType !== "all" 
                ? "Tente outro termo de pesquisa ou filtro" 
                : "Comece adicionando o seu primeiro gabinete"}
            </p>
            {!search && filterType === "all" && (
              <Button className="btn-primary" onClick={openCreateDialog}>
                <Plus size={18} />
                Adicionar Gabinete
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4 stagger-animation">
          {filteredGabinetes.map((gabinete) => {
            const typeConfig = getTypeConfig(gabinete.gabinete_type);
            const TypeIcon = typeConfig.icon;
            
            return (
              <div key={gabinete.id} className="gresart-card-interactive group relative" data-testid={`gabinete-card-${gabinete.id}`}>
                {/* Clickable area */}
                <Link to={`/promocao/${gabinete.id}`} className="block p-2 sm:p-4">
                  <div className="flex flex-col items-center text-center">
                    {gabinete.logo_url ? (
                      <OptimizedImage 
                        src={gabinete.logo_url} 
                        alt={`Logo ${gabinete.name}`}
                        className="w-16 h-16 sm:w-24 sm:h-24 object-contain bg-white rounded-lg mb-2 sm:mb-3"
                        width={150}
                      />
                    ) : (
                      <div className="w-16 h-16 sm:w-24 sm:h-24 bg-stone-100 rounded-lg flex items-center justify-center mb-2 sm:mb-3">
                        <span className="text-xl sm:text-3xl font-bold text-stone-400">
                          {gabinete.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <h3 className="font-semibold text-stone-900 hover:text-[#00A19A] transition-colors text-xs sm:text-base line-clamp-2">
                      {gabinete.name}
                    </h3>
                    
                    {/* Type Badge */}
                    <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white ${typeConfig.color}`}>
                      <TypeIcon size={12} weight="bold" />
                      {typeConfig.label}
                    </div>

                    {/* Locality */}
                    {gabinete.city && (
                      <p className="mt-1 text-xs text-stone-400">{gabinete.city}</p>
                    )}
                  </div>
                </Link>

                {/* Action buttons - visible on hover only, matches Clientes */}
                <div className="absolute top-1 right-1 sm:top-2 sm:right-2 flex gap-0.5 sm:gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditDialog(gabinete); }}
                    className="p-1 sm:p-1.5 bg-white/90 text-stone-400 hover:text-[#00A19A] hover:bg-teal-50 rounded-full shadow-sm transition-colors"
                    data-testid={`btn-edit-${gabinete.id}`}
                  >
                    <PencilSimple size={14} className="sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteId(gabinete.id); }}
                    className="p-1 sm:p-1.5 bg-white/90 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-full shadow-sm transition-colors"
                    data-testid={`btn-delete-${gabinete.id}`}
                  >
                    <Trash size={14} className="sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gabinete-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editingGabinete ? "Editar Gabinete" : "Novo Gabinete"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Logo and Type in same row */}
              <div className="flex gap-4 items-start">
                {/* Logo Upload */}
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <div className="relative">
                      <img 
                        src={logoPreview} 
                        alt="Logo" 
                        className="w-16 h-16 object-contain border border-stone-200 rounded-lg bg-white"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => logoInputRef.current?.click()}
                      className="w-16 h-16 border-2 border-dashed border-stone-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#00A19A] hover:bg-teal-50/30 transition-colors"
                    >
                      {uploadingLogo ? (
                        <LoadingSpinner size={20} />
                      ) : (
                        <ImageIcon size={20} className="text-stone-400" />
                      )}
                    </div>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleLogoUpload(e.target.files[0])}
                  />
                </div>

                {/* Type Select */}
                <div className="flex-1">
                  <Label>Tipo *</Label>
                  <Select 
                    value={formData.gabinete_type} 
                    onValueChange={(v) => setFormData({...formData, gabinete_type: v})}
                  >
                    <SelectTrigger className="mt-1" data-testid="select-gabinete-type">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {GABINETE_TYPES.map(type => {
                        const IconComp = type.icon;
                        return (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <IconComp size={16} className="text-stone-500" />
                              {type.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="name">Nome da Empresa *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="gresart-input mt-1"
                  placeholder="Nome do gabinete"
                  data-testid="input-gabinete-name"
                />
              </div>

              <div>
                <Label htmlFor="address">Morada</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="gresart-input mt-1"
                  placeholder="Endereço"
                  data-testid="input-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="postal_code">Código Postal</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="gresart-input mt-1"
                    placeholder="0000-000"
                    data-testid="input-postal-code"
                  />
                </div>
                <div>
                  <Label htmlFor="city">Localidade</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="gresart-input mt-1"
                    placeholder="Cidade"
                    data-testid="input-city"
                  />
                </div>
              </div>

              {/* Pessoas de Contacto - full width, right after Nome/Morada/Codigo Postal/Localidade */}
              <div>
                <Label>Pessoas de Contacto</Label>
                <div className="space-y-2 mt-1">
                  {formData.contacts.map((c, idx) => (
                    <div key={idx} className="grid grid-cols-[2fr_1.4fr_1.4fr_auto] gap-3">
                      <Input placeholder="Nome" value={c.name} onChange={(e) => updateContact(idx, 'name', e.target.value)} className="gresart-input" data-testid={`input-contact-name-${idx}`} />
                      <Input placeholder="Cargo" value={c.cargo || ""} onChange={(e) => updateContact(idx, 'cargo', e.target.value)} className="gresart-input" data-testid={`input-contact-cargo-${idx}`} />
                      <Input placeholder="Telefone" value={c.phone || ""} onChange={(e) => updateContact(idx, 'phone', e.target.value)} className="gresart-input" data-testid={`input-contact-phone-${idx}`} />
                      <Button
                        type="button" variant="ghost" size="icon"
                        className="text-stone-400 hover:text-red-600 disabled:opacity-30"
                        disabled={formData.contacts.length <= 1}
                        onClick={() => removeContact(idx)}
                        data-testid={`btn-remove-contact-${idx}`}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addContact} data-testid="btn-add-contact">
                  <Plus size={14} className="mr-1" /> Adicionar Pessoa de Contacto
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="country">País</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="gresart-input mt-1"
                        data-testid="input-country"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gps_coordinates">Coordenadas GPS</Label>
                      <Input
                        id="gps_coordinates"
                        value={formData.gps_coordinates}
                        onChange={(e) => setFormData({ ...formData, gps_coordinates: e.target.value })}
                        className="gresart-input mt-1"
                        placeholder="41.1579, -8.6291"
                        data-testid="input-gps-coordinates"
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column - Contact Info */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="gresart-input mt-1"
                      placeholder="email@exemplo.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>
              </div>

              {/* Web & Redes Sociais - full width, split evenly in half */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="website">Web</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="gresart-input mt-1"
                    placeholder="www.empresa.com"
                    data-testid="input-website"
                  />
                </div>

                <div>
                  <Label htmlFor="social_media">Redes Sociais</Label>
                  <Input
                    id="social_media"
                    value={formData.social_media}
                    onChange={(e) => setFormData({ ...formData, social_media: e.target.value })}
                    className="gresart-input mt-1"
                    placeholder="@instagram, Facebook, etc."
                    data-testid="input-social-media"
                  />
                </div>
              </div>

              {/* Notas internas - full width */}
              <div>
                <Label htmlFor="perfil_interno">Notas</Label>
                <Textarea
                  id="perfil_interno"
                  value={formData.perfil_interno}
                  onChange={(e) => setFormData({ ...formData, perfil_interno: e.target.value })}
                  className="gresart-input mt-1 resize-y"
                  data-testid="input-perfil-interno"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-stone-100">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="btn-cancel">
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="btn-save">
                {saving ? "A guardar..." : (editingGabinete ? "Atualizar" : "Criar")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar gabinete?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Serão também eliminadas todas as visitas e entregas de catálogos associadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
